import { Typenames } from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

const SuspiciousTransactionResult = () => {
  const router = useRouter();
  const { resultType, title, message } = useLocalSearchParams<{
    resultType:
      | Typenames.SuspiciousCardTxnActionApproveSuccess
      | Typenames.SuspiciousCardTxnActionDeclineSuccess
      | Typenames.SuspiciousCardTxnActionError;
    title?: string;
    message?: string;
  }>();

  const { t: translatedText } = useTranslation('suspiciousTransaction', {
    keyPrefix: 'suspiciousTransactionResult',
  });

  const { cancelButtonTitle } = useDefaultErrorStrings();

  let analyticsEvent = 'card_replaced';
  if (resultType === Typenames.SuspiciousCardTxnActionError) {
    analyticsEvent = 'card_replacement_error';
  } else if (resultType === Typenames.SuspiciousCardTxnActionApproveSuccess) {
    analyticsEvent = 'transaction_recognized';
  }

  useViewDidFocusScreenAnalyticsEvent({
    key: analyticsEvent,
  });

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      {resultType === Typenames.SuspiciousCardTxnActionError ? (
        <View style={styles.errorWrapper}>
          <ErrorScreen
            screenType={ErrorScreenType.Error}
            messageHeadline={title}
            messageBody={message}
            primaryButton={{
              title: cancelButtonTitle,
              onPress: () => router.navigate('/'),
              analytics: {
                analyticsId: 'suspicious_transaction_done',
                customAttributes: {
                  cta: cancelButtonTitle,
                },
              },
            }}
          />
        </View>
      ) : null}
      {resultType === Typenames.SuspiciousCardTxnActionApproveSuccess ? (
        <SuccessScreen
          visual="CARD_B"
          titleText={title || ''}
          content={
            <BodyBaseText numberOfLines={0} style={styles.description}>
              {message}
            </BodyBaseText>
          }
          primaryButton={{
            label: translatedText('done'),
            onPress: () => router.replace('/'),
            analyticsId: 'suspicious_transaction_done',
            customAttributes: {
              cta: translatedText('done'),
            },
          }}
        />
      ) : null}
      {resultType === Typenames.SuspiciousCardTxnActionDeclineSuccess ? (
        <SuccessScreen
          visual="CARD_SENT_A"
          titleText={translatedText('cardReplaced.title')}
          content={
            <BodyBaseText numberOfLines={0} style={styles.description}>
              {translatedText('cardReplaced.description')}
            </BodyBaseText>
          }
          primaryButton={{
            label: translatedText('done'),
            onPress: () => router.replace('/'),
            analyticsId: 'suspicious_transaction_done',
            customAttributes: {
              cta: translatedText('done'),
            },
          }}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  description: {
    textAlign: 'center',
  },
  errorWrapper: {
    flex: 1,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
});

export default SuspiciousTransactionResult;
